import {
  type Lead,
  LeadSource,
  LeadStatus,
  LeadsApi,
} from '@up2stay/up2promo-api-client';
import { Badge, Button, Checkbox } from '@up2stay/up2promo-ui';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { PhoneInput } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Check,
  Mail,
  MessageSquare,
  Phone,
  ScanLine,
  User,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import de from 'react-phone-number-input/locale/de';
import en from 'react-phone-number-input/locale/en';
import { toast } from 'sonner';

import { invalidateStatusBoardQueries } from '@/core/components/dashboard/dashboard-refresh-keys';
import {
  LeadCardMetaDetails,
  type LeadCardMetaLines,
} from '@/core/components/leads/lead-card-content';
import {
  type LeadCardLabelMaps,
  LeadCardLabels,
} from '@/core/components/leads/lead-card-labels';
import { LeadCardQuickMenu } from '@/core/components/leads/lead-card-quick-menu';
import { LeadDetailDeleteDialog } from '@/core/components/leads/lead-detail/lead-detail-delete-dialog';
import { LeadStatusBadge } from '@/core/components/leads/lead-detail/lead-status-badge';
import { LeadDuplicateResolutionDialog } from '@/core/components/leads/lead-duplicate-resolution-dialog';
import {
  LEAD_EVENT_CAPTURE_NONE,
  LeadEventCapturePicker,
} from '@/core/components/leads/lead-event-capture-picker';
import { GridAddCard } from '@/core/components/layout/grid-add-card';
import {
  resourceInteractiveCardClass,
  resourceJustCreatedCardClass,
  resourceQuickMenuActiveCardClass,
  RESOURCE_JUST_CREATED_HIGHLIGHT_MS,
} from '@/core/components/layout/list-toolbar-styles';
import { HighlightMatchText } from '@/core/components/ui/highlight-match-text';
import { useActiveCaptureEvents } from '@/core/hooks/use-active-capture-events';
import { useLeadDuplicateResolution } from '@/core/hooks/use-lead-duplicate-resolution';
import { useLocale } from '@/core/hooks/use-locale';
import { notificationUnreadCountQueryKey } from '@/core/hooks/use-notifications';
import {
  triggerErrorHaptic,
  triggerSuccessHaptic,
} from '@/core/lib/dnd-haptics';
import { isValidLeadEmail } from '@/core/lib/lead-email';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { leadsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { getApiValidationErrorCode } from '@/core/utils/api-error.utils';

const CREATE_LEAD_FIELD_CLASS =
  'relative w-full min-w-0 md:min-w-44 md:w-auto md:max-w-none md:flex-1';

const CREATE_LEAD_INPUT_ICON_CLASS =
  'pointer-events-none absolute left-3 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground';

interface LeadCardListProps {
  leads: Lead[];
  existingLeads?: Lead[];
  profileId?: string | null;
  workspaceId?: string | null;
  leadCardMeta?: (lead: Lead) => LeadCardMetaLines | null | undefined;
  labelMaps?: LeadCardLabelMaps;
  /** Active list search — matching substrings are highlighted in card fields */
  searchQuery?: string;
  onLeadClick: (lead: Lead) => void;
  onLeadCreated: (leadId?: string) => void;
  /** Parent-driven flash (e.g. business-card scan) in addition to inline create. */
  highlightedLeadId?: string | null;
  onScanBusinessCard?: () => void;
  /** Link these events when creating a lead from an event page (DEV-453). */
  eventIds?: string[];
  selectionEnabled?: boolean;
  selectedLeadIds?: ReadonlySet<string>;
  onToggleLeadSelect?: (leadId: string) => void;
  selectAriaLabel?: string;
  /** Hide GridAddCard row when header CTAs own create/scan. */
  hideAddCards?: boolean;
  /** Skip stacked lead cards (table/board owns the rows). */
  hideLeadCards?: boolean;
  /** Increment to open the inline create form from an external CTA. */
  createRequestId?: number;
  /** Status for a `createRequestId` open (board column); defaults to Neu. */
  createInitialStatus?: LeadStatus;
}

export function LeadCardList({
  leads,
  existingLeads,
  profileId,
  workspaceId,
  leadCardMeta,
  labelMaps,
  searchQuery,
  onLeadClick,
  onLeadCreated,
  highlightedLeadId = null,
  onScanBusinessCard,
  eventIds,
  selectionEnabled = false,
  selectedLeadIds,
  onToggleLeadSelect,
  selectAriaLabel,
  hideAddCards = false,
  hideLeadCards = false,
  createRequestId = 0,
  createInitialStatus,
}: LeadCardListProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const phoneLabels = locale === 'de' ? de : en;
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showFirstNameError, setShowFirstNameError] = useState(false);
  const [capturePickerOpen, setCapturePickerOpen] = useState(false);
  const [capturePickerValue, setCapturePickerValue] = useState('');
  const pendingCreateWithEventsRef = useRef<
    ((resolvedEventIds: string[] | undefined) => Promise<void>) | null
  >(null);
  const [newLead, setNewLead] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
  });
  const [newLeadStatus, setNewLeadStatus] = useState<LeadStatus>(
    LeadStatus.NEW,
  );
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  // Field message instead of the generic 422 toast / native bubble (DEV-571).
  const [showEmailError, setShowEmailError] = useState(false);
  const createCardRef = useRef<HTMLDivElement>(null);
  /** Bumped on every "add lead" request so an already open form refocuses. */
  const [createFocusTick, setCreateFocusTick] = useState(0);
  const listRootRef = useRef<HTMLDivElement>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [justCreatedLeadId, setJustCreatedLeadId] = useState<string | null>(
    null,
  );
  const [leadPendingDelete, setLeadPendingDelete] = useState<Lead | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { candidates: activeCaptureEvents } = useActiveCaptureEvents(
    workspaceId ?? undefined,
    Boolean(workspaceId) && (isCreating || capturePickerOpen),
  );

  const flashLeadId = justCreatedLeadId ?? highlightedLeadId ?? null;

  const notifyLeadCreated = useCallback(
    (leadId?: string) => {
      if (leadId) {
        void queryClient.invalidateQueries({
          queryKey: ['lead', leadId],
        });
        setJustCreatedLeadId(leadId);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setJustCreatedLeadId(null);
          highlightTimeoutRef.current = null;
        }, RESOURCE_JUST_CREATED_HIGHLIGHT_MS);
      }
      onLeadCreated(leadId);
    },
    [onLeadCreated, queryClient],
  );

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!flashLeadId) return;
    const card = listRootRef.current?.querySelector(
      `[data-lead-id="${CSS.escape(flashLeadId)}"]`,
    );
    card?.scrollIntoView({ block: 'nearest' });
  }, [flashLeadId, leads]);

  const handleQuickStatus = useCallback(
    async (lead: Lead, status: LeadStatus) => {
      if (!workspaceId) return;
      const toastDict = (
        dict.leads.detail as { toast?: Record<string, string> }
      ).toast;
      try {
        await LeadsApi.update({
          path: { workspaceId, id: lead.id },
          body: { status },
        });
        void triggerSuccessHaptic();
        onLeadCreated();
      } catch {
        void triggerErrorHaptic();
        toast.error(toastDict?.statusUpdateFailed ?? 'Update failed');
      }
    },
    [dict.leads.detail, onLeadCreated, workspaceId],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!leadPendingDelete || !workspaceId) return;
    const toastDict = (dict.leads.detail as { toast?: Record<string, string> })
      .toast;
    try {
      setIsDeleting(true);
      void triggerErrorHaptic();
      await LeadsApi.remove({
        path: { workspaceId, id: leadPendingDelete.id },
      });
      void triggerSuccessHaptic();
      setLeadPendingDelete(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root }),
        invalidateStatusBoardQueries(queryClient),
        queryClient.invalidateQueries({
          queryKey: ['notifications', 'inbox', workspaceId],
        }),
        queryClient.invalidateQueries({
          queryKey: notificationUnreadCountQueryKey(workspaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: ['dashboard', 'leads', workspaceId],
        }),
      ]);
      onLeadCreated();
    } catch {
      void triggerErrorHaptic();
      toast.error(toastDict?.leadDeleteFailed ?? 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  }, [
    dict.leads.detail,
    leadPendingDelete,
    onLeadCreated,
    queryClient,
    workspaceId,
  ]);

  const createDict = dict.leads.create as Record<string, string>;
  const detailDict = dict.leads.detail;
  const duplicateDict = dict.leads.duplicate as Record<string, unknown>;
  const scanDict = dict.leads.businessCardScan as unknown as Record<
    string,
    string
  >;
  const leadsForDuplicateCheck = existingLeads ?? leads;

  const duplicateLabels = useMemo(
    () => ({
      title: String(duplicateDict.title),
      description: String(duplicateDict.description),
      selectLead: String(duplicateDict.selectLead),
      matchReason: (duplicateDict.matchReason ?? {}) as Record<
        'email' | 'phone' | 'nameCompany',
        string
      >,
      chooseAction: String(duplicateDict.chooseAction ?? 'What should happen?'),
      useExisting: String(duplicateDict.useExisting ?? 'Use this lead'),
      useExistingDescription: String(
        duplicateDict.useExistingDescription ??
          'Keep the existing contact as it is and continue.',
      ),
      supplement: String(duplicateDict.supplement),
      supplementDescription: String(duplicateDict.supplementDescription),
      replace: String(duplicateDict.replace),
      replaceDescription: String(duplicateDict.replaceDescription),
      createNew: String(duplicateDict.createNew ?? 'Save as new lead'),
      createNewDescription: String(
        duplicateDict.createNewDescription ??
          'Just create a new lead — the existing one stays unchanged.',
      ),
      moreOptions: String(duplicateDict.moreOptions ?? 'Change existing lead…'),
      continue: String(duplicateDict.continue ?? 'Continue'),
      cancel: String(duplicateDict.cancel ?? duplicateDict.discard ?? 'Cancel'),
      discard: String(duplicateDict.discard ?? 'Cancel'),
      processing: String(duplicateDict.processing),
      supplementSuccess: String(duplicateDict.supplementSuccess),
      replaceSuccess: String(duplicateDict.replaceSuccess),
      createNewSuccess: String(
        duplicateDict.createNewSuccess ?? 'Saved as new lead',
      ),
      useExistingSuccess: String(
        duplicateDict.useExistingSuccess ?? 'Using existing lead',
      ),
      actionFailed: String(duplicateDict.actionFailed),
    }),
    [duplicateDict],
  );

  const getStatusLabel = (status: string) =>
    dict.leads.status[status as keyof typeof dict.leads.status] || status;

  const { attemptCreate, dialogProps } = useLeadDuplicateResolution({
    existingLeads: leadsForDuplicateCheck,
    labels: duplicateLabels,
    getStatusLabel,
    onResolved: (leadId) => {
      setIsCreating(false);
      setShowFirstNameError(false);
      setNewLeadStatus(LeadStatus.NEW);
      setNewLead({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobTitle: '',
      });
      notifyLeadCreated(leadId);
    },
  });

  const getSourceLabel = (source: string) =>
    (dict.leads.list.source as Record<string, string>)?.[source] || source;

  const handleStartCreating = useCallback(
    (status: LeadStatus = LeadStatus.NEW) => {
      setIsCreating(true);
      setShowFirstNameError(false);
      setNewLeadStatus(status);
      setNewLead({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        jobTitle: '',
      });
      setCreateFocusTick((tick) => tick + 1);
    },
    [],
  );

  // The form renders below the list: once it is mounted, bring it into view
  // and focus "Vorname" (a rAF focus ran before the form existed, DEV-554).
  useEffect(() => {
    if (!isCreating || createFocusTick === 0) return;
    createCardRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
    nameInputRef.current?.focus({ preventScroll: true });
  }, [isCreating, createFocusTick]);

  const lastCreateRequestIdRef = useRef(0);
  useEffect(() => {
    if (createRequestId <= 0) return;
    if (createRequestId === lastCreateRequestIdRef.current) return;
    lastCreateRequestIdRef.current = createRequestId;
    handleStartCreating(createInitialStatus);
  }, [createRequestId, createInitialStatus, handleStartCreating]);

  const handleCancelCreating = () => {
    setIsCreating(false);
    setShowFirstNameError(false);
    setShowEmailError(false);
    setNewLeadStatus(LeadStatus.NEW);
    setNewLead({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
    });
  };

  const handleSaveNewLead = async () => {
    if (!newLead.firstName.trim()) {
      setShowFirstNameError(true);
      void triggerErrorHaptic();
      nameInputRef.current?.focus();
      return;
    }
    setShowFirstNameError(false);
    const emailDraft = newLead.email.trim();
    if (emailDraft && !isValidLeadEmail(emailDraft)) {
      setShowEmailError(true);
      void triggerErrorHaptic();
      emailInputRef.current?.focus();
      return;
    }
    setShowEmailError(false);

    const runCreate = async (resolvedEventIds: string[] | undefined) => {
      try {
        setIsSaving(true);
        const emailTrimmed = newLead.email.trim();
        const phoneTrimmed = newLead.phone.trim();
        const lastNameTrimmed = newLead.lastName.trim();
        const jobTitleTrimmed = newLead.jobTitle.trim();

        const leadId = await attemptCreate({
          ...(profileId ? { profileId } : {}),
          ...(workspaceId ? { workspaceId } : {}),
          firstName: newLead.firstName.trim(),
          ...(lastNameTrimmed && { lastName: lastNameTrimmed }),
          ...(emailTrimmed && { email: emailTrimmed }),
          ...(phoneTrimmed && { phone: phoneTrimmed }),
          ...(jobTitleTrimmed && { jobTitle: jobTitleTrimmed }),
          status: newLeadStatus,
          source: LeadSource.MANUAL,
          ...(resolvedEventIds?.length ? { eventIds: resolvedEventIds } : {}),
        });

        if (leadId) {
          void triggerSuccessHaptic();
          setIsCreating(false);
          setShowFirstNameError(false);
          setNewLeadStatus(LeadStatus.NEW);
          setNewLead({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            jobTitle: '',
          });
          notifyLeadCreated(leadId);
        }
      } catch (error) {
        void triggerErrorHaptic();
        if (getApiValidationErrorCode(error, 'email')) {
          setShowEmailError(true);
          emailInputRef.current?.focus();
          return;
        }
        console.error('Failed to create lead:', error);
        toast.error(createDict.saveFailed);
      } finally {
        setIsSaving(false);
      }
    };

    if (eventIds?.length) {
      await runCreate(eventIds);
      return;
    }

    if (activeCaptureEvents.length > 1) {
      pendingCreateWithEventsRef.current = runCreate;
      setCapturePickerValue(activeCaptureEvents[0]!.id);
      setCapturePickerOpen(true);
      return;
    }

    if (activeCaptureEvents.length === 1) {
      await runCreate([activeCaptureEvents[0]!.id]);
      return;
    }

    await runCreate(undefined);
  };

  const capturePickerDict = (
    dict.leads.events as {
      capturePicker?: {
        title: string;
        none: string;
        confirm: string;
        cancel: string;
      };
    }
  ).capturePicker;

  return (
    <div ref={listRootRef} className="flex flex-col gap-4">
      <LeadDuplicateResolutionDialog {...dialogProps} />
      <LeadEventCapturePicker
        open={capturePickerOpen}
        onOpenChange={(open) => {
          setCapturePickerOpen(open);
          if (!open) pendingCreateWithEventsRef.current = null;
        }}
        events={activeCaptureEvents}
        value={capturePickerValue}
        onChange={setCapturePickerValue}
        onConfirm={() => {
          const run = pendingCreateWithEventsRef.current;
          const picked = capturePickerValue;
          setCapturePickerOpen(false);
          pendingCreateWithEventsRef.current = null;
          const ids =
            picked === LEAD_EVENT_CAPTURE_NONE || !picked
              ? undefined
              : [picked];
          void run?.(ids);
        }}
        labels={{
          title:
            capturePickerDict?.title ?? 'Zu welchem Event gehört der Lead?',
          none: capturePickerDict?.none ?? 'Keinem Event',
          confirm: capturePickerDict?.confirm ?? 'Weiter',
          cancel: capturePickerDict?.cancel ?? 'Abbrechen',
        }}
      />
      <LeadDetailDeleteDialog
        open={leadPendingDelete != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLeadPendingDelete(null);
        }}
        title={detailDict.deleteConfirmTitle}
        description={detailDict.deleteConfirmDescription}
        cancelLabel={dict.settings.cancel}
        confirmLabel={detailDict.deleteConfirm}
        savingLabel={dict.settings.saving}
        isDeleting={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
      />
      {/* Dashed add card */}
      {!isCreating && !hideAddCards ? (
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <GridAddCard
            label={createDict.addCard ?? 'Add lead'}
            onClick={() => handleStartCreating()}
            size="compact"
          />
          {onScanBusinessCard ? (
            <GridAddCard
              label={scanDict.button ?? 'Scan'}
              onClick={onScanBusinessCard}
              size="compact"
              icon={<ScanLine className="h-5 w-5 shrink-0" aria-hidden />}
            />
          ) : null}
        </div>
      ) : null}

      {/* Inline creation card */}
      {isCreating && (
        <Card
          ref={createCardRef}
          className="scroll-mt-24 border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]/40"
        >
          <CardContent className="p-5 sm:p-6">
            {newLeadStatus !== LeadStatus.NEW ? (
              // Board column "+ Lead": show where the lead will land (DEV-554).
              <div className="mb-3 flex items-center gap-2 text-[13px] text-muted-foreground">
                {createDict.targetStatus ?? 'Status'}
                <LeadStatusBadge
                  status={newLeadStatus}
                  label={getStatusLabel(newLeadStatus)}
                  className="h-[22px] rounded-full px-2 text-[11.5px] font-semibold"
                />
              </div>
            ) : null}
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void handleSaveNewLead();
              }}
              className={cn(
                'flex w-full flex-col gap-2.5',
                // items-start: the first-name error must not push the other
                // fields down (DEV-567).
                'md:flex-row md:flex-wrap md:items-start md:gap-3',
              )}
            >
              <div
                className={cn(
                  CREATE_LEAD_FIELD_CLASS,
                  'flex flex-col gap-2 min-[520px]:flex-row md:min-w-72 md:flex-[1.25]',
                )}
              >
                <div className="min-w-0 flex-1">
                  {/* Icon centres on the input only, not input + error. */}
                  <div className="relative">
                    <User className={CREATE_LEAD_INPUT_ICON_CLASS} />
                    <Input
                      ref={nameInputRef}
                      value={newLead.firstName}
                      onChange={(e) => {
                        setNewLead({ ...newLead, firstName: e.target.value });
                        if (showFirstNameError && e.target.value.trim()) {
                          setShowFirstNameError(false);
                        }
                      }}
                      placeholder={createDict.firstName}
                      aria-label={createDict.firstName}
                      className="h-10 w-full pl-9 font-semibold text-sm"
                      aria-invalid={showFirstNameError}
                      aria-describedby={
                        showFirstNameError ? 'lead-first-name-error' : undefined
                      }
                    />
                  </div>
                  {showFirstNameError ? (
                    <p
                      id="lead-first-name-error"
                      className="mt-1 text-xs text-destructive"
                      role="alert"
                    >
                      {dict.leads.form.errors.firstNameRequired}
                    </p>
                  ) : null}
                </div>
                <Input
                  value={newLead.lastName}
                  onChange={(e) =>
                    setNewLead({ ...newLead, lastName: e.target.value })
                  }
                  placeholder={createDict.lastName}
                  aria-label={createDict.lastName}
                  className="h-10 min-w-0 flex-1 font-semibold text-sm"
                />
              </div>

              <div className={CREATE_LEAD_FIELD_CLASS}>
                <div className="relative">
                  <Mail className={CREATE_LEAD_INPUT_ICON_CLASS} />
                  <Input
                    ref={emailInputRef}
                    type="email"
                    inputMode="email"
                    value={newLead.email}
                    onChange={(e) => {
                      setNewLead({ ...newLead, email: e.target.value });
                      if (
                        showEmailError &&
                        (!e.target.value.trim() ||
                          isValidLeadEmail(e.target.value))
                      ) {
                        setShowEmailError(false);
                      }
                    }}
                    placeholder={createDict.email}
                    aria-label={createDict.email}
                    className="h-10 w-full pl-9 text-sm"
                    aria-invalid={showEmailError}
                    aria-describedby={
                      showEmailError ? 'lead-email-error' : undefined
                    }
                  />
                </div>
                {showEmailError ? (
                  <p
                    id="lead-email-error"
                    className="mt-1 text-xs text-destructive"
                    role="alert"
                  >
                    {dict.leads.form.errors.emailInvalid}
                  </p>
                ) : null}
              </div>

              <div className={CREATE_LEAD_FIELD_CLASS}>
                <Phone className="pointer-events-none absolute left-[4.75rem] top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <PhoneInput
                  defaultCountry="DE"
                  labels={phoneLabels}
                  placeholder={createDict.phone}
                  aria-label={createDict.phone}
                  value={newLead.phone || undefined}
                  onChange={(v) => setNewLead({ ...newLead, phone: v || '' })}
                  className="[&_input]:pl-9"
                />
              </div>

              <div className={CREATE_LEAD_FIELD_CLASS}>
                <Briefcase className={CREATE_LEAD_INPUT_ICON_CLASS} />
                <Input
                  value={newLead.jobTitle}
                  onChange={(e) =>
                    setNewLead({ ...newLead, jobTitle: e.target.value })
                  }
                  placeholder={createDict.jobTitle}
                  aria-label={createDict.jobTitle}
                  className="h-10 w-full pl-9 text-sm"
                />
              </div>

              <div className="flex shrink-0 items-center gap-2 md:ml-auto">
                <Button
                  type="submit"
                  size="sm"
                  disabled={isSaving}
                  className="min-h-11 gap-1.5"
                >
                  <Check className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  {createDict.inlineSave}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelCreating}
                  disabled={isSaving}
                  className="min-h-11 px-3 text-muted-foreground"
                >
                  {createDict.inlineCancel}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Existing lead cards — vertical list; desktop shows extra column */}
      {!hideLeadCards
        ? leads.map((lead) => {
            const selected = selectedLeadIds?.has(lead.id) === true;
            const justCreated = flashLeadId === lead.id;
            return (
              <LeadCardQuickMenu
                key={lead.id}
                lead={lead}
                onOpenLead={() => onLeadClick(lead)}
                onSetStatus={(status) => {
                  void handleQuickStatus(lead, status);
                }}
                onDelete={() => setLeadPendingDelete(lead)}
              >
                {({ highlighted }) => (
                  <Card
                    data-lead-id={lead.id}
                    data-just-created={justCreated ? 'true' : undefined}
                    data-quick-menu-active={highlighted ? 'true' : undefined}
                    className={cn(
                      'cursor-pointer',
                      resourceInteractiveCardClass,
                      selected &&
                        'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]',
                      justCreated && resourceJustCreatedCardClass,
                      highlighted && resourceQuickMenuActiveCardClass,
                    )}
                  >
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-8">
                        <div className="flex min-w-0 flex-1 gap-3">
                          {selectionEnabled ? (
                            <Checkbox
                              checked={selected}
                              className="mt-1 shrink-0"
                              aria-label={selectAriaLabel ?? 'Select lead'}
                              onCheckedChange={() =>
                                onToggleLeadSelect?.(lead.id)
                              }
                              onClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => e.stopPropagation()}
                            />
                          ) : null}
                          <div className="flex min-w-0 flex-1 flex-col gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <HighlightMatchText
                                text={
                                  formatLeadDisplayName(
                                    lead.firstName,
                                    lead.lastName,
                                  ) ||
                                  lead.email ||
                                  '—'
                                }
                                query={searchQuery}
                                className="truncate text-[15.5px] font-bold leading-tight text-foreground"
                              />
                              <LeadStatusBadge
                                status={lead.status}
                                label={getStatusLabel(lead.status)}
                              />
                            </div>

                            {lead.companyName ? (
                              <p className="truncate text-[13.5px] leading-relaxed text-muted-foreground">
                                <HighlightMatchText
                                  text={lead.companyName}
                                  query={searchQuery}
                                />
                              </p>
                            ) : null}

                            <div className="flex flex-col gap-0.5 text-[13.5px] leading-relaxed text-muted-foreground">
                              {lead.email ? (
                                <div className="flex min-w-0 items-center gap-2">
                                  <Mail className="h-3 w-3 shrink-0" />
                                  <HighlightMatchText
                                    text={lead.email}
                                    query={searchQuery}
                                    className="truncate"
                                  />
                                </div>
                              ) : null}
                              {lead.phone ? (
                                <div className="flex min-w-0 items-center gap-2">
                                  <Phone className="h-3 w-3 shrink-0" />
                                  <HighlightMatchText
                                    text={lead.phone}
                                    query={searchQuery}
                                    className="truncate"
                                  />
                                </div>
                              ) : null}
                            </div>

                            <LeadCardLabels
                              lead={lead}
                              maps={labelMaps}
                              searchQuery={searchQuery}
                            />

                            {(() => {
                              const meta = leadCardMeta?.(lead);
                              if (!meta) return null;
                              return <LeadCardMetaDetails meta={meta} />;
                            })()}

                            <p className="text-xs text-muted-foreground md:hidden">
                              {lead.createdAt
                                ? new Date(lead.createdAt).toLocaleDateString(
                                    undefined,
                                    {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    },
                                  )
                                : null}
                            </p>
                          </div>
                        </div>

                        {/* Desktop-only: source, time, message preview */}
                        <div className="hidden max-w-[min(100%,280px)] shrink-0 flex-col items-end gap-2 text-right md:flex">
                          {!leadCardMeta?.(lead) ? (
                            <Badge
                              variant="outline"
                              className="text-xs font-normal"
                            >
                              {getSourceLabel(lead.source)}
                            </Badge>
                          ) : null}
                          {lead.createdAt ? (
                            <p className="whitespace-nowrap text-xs text-muted-foreground">
                              {new Date(lead.createdAt).toLocaleString(
                                undefined,
                                {
                                  dateStyle: 'medium',
                                  timeStyle: 'short',
                                },
                              )}
                            </p>
                          ) : null}
                          {lead.message ? (
                            <div className="flex w-full justify-end gap-1.5 text-left">
                              <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">
                                {lead.message}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </LeadCardQuickMenu>
            );
          })
        : null}
    </div>
  );
}
